import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCommentSlash } from '@fortawesome/free-solid-svg-icons/faCommentSlash';
import Tooltip from 'rc-tooltip';

import Button, { BUTTON_TYPES } from '@buyside/components/presentation/Button/button';
import IdeaViewsBreakdown from '@buyside/components/presentation/IdeaViewsPage/IdeaViewsBreakdown/ideaViewsBreakdown';
import IdeaViewsIdeaInfo from '@buyside/components/presentation/IdeaViewsPage/IdeaViewsIdeaInfo/ideaViewsIdeaInfo';
import IdeaViewsList from '@buyside/components/presentation/IdeaViewsPage/IdeaViewsList/ideaViewsList';
import LoadingSpinner, {
  SIZES
} from '@buyside/components/presentation/LoadingSpinner/loadingSpinner';

import { useIdeaViewsQuery } from '@interfaces/graphql-typings';

import styles from '@buyside/components/presentation/ContributorDashboard/contributorDashboardStyles.scss';

const MyIdeaPane: React.FC<{ ideaId: string }> = ({ ideaId }) => {
  const { error, data, loading, fetchMore } = useIdeaViewsQuery({
    variables: { id: ideaId }
  });

  if (loading)
    return (
      <div className={styles.ideaDetails}>
        <LoadingSpinner size={SIZES.LARGE} />
      </div>
    );

  if (error) throw error;

  if (!data || !data.idea) return null;

  const views = data.idea.ideaViews?.edges?.map(edge => edge?.node && edge.node);
  const pageInfo = data.idea.ideaViews?.pageInfo;

  return (
    <div className={styles.ideaDetails}>
      <IdeaViewsIdeaInfo idea={data.idea} />
      {data.idea.isCurrentUserAuthor ? (
        <>
          {views?.length ? (
            <>
              <IdeaViewsList
                views={views}
                showIntroRequest={!data.idea.anonymous}
                isBig
                fullText={false}
                introRequestDisabled={
                  data.idea.anonymous ? (
                    <Tooltip
                      overlay={
                        <p>Intro requests are disabled because this idea was posted anonymously.</p>
                      }
                    >
                      <FontAwesomeIcon icon={faCommentSlash} className={styles.msgSlashIcon} />
                    </Tooltip>
                  ) : null
                }
              />
              {pageInfo?.hasNextPage && (
                <Button
                  id="load-more-views"
                  className={styles.loadMoreButton}
                  onClick={(): void => {
                    fetchMore({ variables: { id: ideaId, cursor: pageInfo?.endCursor } });
                  }}
                  text="Load More"
                  type={BUTTON_TYPES.PRIMARY}
                />
              )}
              <IdeaViewsBreakdown views={views} />
            </>
          ) : (
            <div className={styles.ideaMsg}>No users have viewed this idea yet.</div>
          )}
        </>
      ) : (
        <div className={styles.ideaMsg}>You do not have access to this information.</div>
      )}
    </div>
  );
};

export default MyIdeaPane;
